<template>
  <div>
    <v-form
      :disabled="form.processing"
      @submit.prevent="submit"
    >
      <v-row>
        <v-col>
          <v-switch
            v-model="form.enabled"
            class="mt-0"
            dense
            color="primary"
            :label="$t('Enable FAQ Portal')"
            :hint="$t('This will make your FAQ available as a separate website that you can configure to match your branding')"
            persistent-hint
          />
        </v-col>
      </v-row>

      <div
        v-if="faq.portal_enabled"
        class="mt-2"
      >
        <span>URL:</span> <a
          target="_blank"
          :href="faq.portal_url"
        >{{ faq.portal_url }}</a>
      </div>

      <template v-if="faq.portal_enabled">
        <v-expansion-panels class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Portal URL') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                class="d-flex flex-wrap"
                style="margin: -12px"
              >
                <div
                  class="flex-grow-1"
                  style="padding: 0 12px 12px 12px"
                >
                  <v-text-field
                    ref="subdomainInput"
                    v-model="validateSubdomainForm.subdomain"
                    class="subdomain"
                    :error-messages="subdomainErrorMessage"
                    :loading="validateSubdomainForm.processing"
                    :disabled="validateSubdomainForm.processing"
                    :placeholder="faq.slug"
                    :success="subdomainValid === true"
                    :success-messages="subdomainSuccessMessage"
                    persistent-placeholder
                    outlined
                    :suffix="`.${portalDomain}`"
                    :hint="$t('Here you may specify a custom subdomain for the portal')"
                    persistent-hint
                    :counter="20"
                    :maxlength="20"
                    @input="handleSubdomainInput"
                    @keydown.enter.prevent="validateSubdomain"
                  />
                </div>
                <div style="padding: 0 12px 12px 12px">
                  <v-btn
                    style="height: 56px"
                    block
                    depressed
                    :loading="validateSubdomainForm.processing"
                    :disabled="validateButtonDisabled"
                    @click="validateSubdomain"
                  >
                    {{ $t('Validate') }}
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Features') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-switch
                    v-model="form.user_feedback"
                    class="mt-0"
                    dense
                    color="primary"
                    :label="$t('User feedback')"
                    :hint="$t('Enabling user feedback will allow users to up or downvote the answer of your questions')"
                    persistent-hint
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-switch
                    v-model="form.contact_info"
                    class="mt-0"
                    dense
                    color="primary"
                    :label="$t('Show contact info block')"
                    :hint="$t(`This will show a block containing the contact info entered under 'General'`)"
                    persistent-hint
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-switch
                    v-model="form.contact_form"
                    class="mt-0 mb-0"
                    dense
                    color="primary"
                    :label="$t('Contact form')"
                    persistent-hint
                    :hint="$t('This setting will show your contact form as a widget in the portal')"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Logotype and favicon') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-select
                    v-model="form.logo_id"
                    :loading="form.processing"
                    :error-messages="form.errors.logo_id"
                    class="mb-2"
                    :items="images"
                    :label="$t('Choose logotype')"
                    item-text="title"
                    item-value="id"
                    outlined
                    clearable
                    :hint="$t('Choose an image from your media library to use as logo (recommended size is 200x55px)')"
                    persistent-hint
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-select
                    v-model="form.favicon_id"
                    :loading="form.processing"
                    :error-messages="form.errors.favicon_id"
                    class="mb-2"
                    :items="images"
                    label="Choose favicon"
                    item-text="title"
                    item-value="id"
                    outlined
                    clearable
                    :hint="$t('Choose an image from your media library to use as favicon (recommended size is 32x32px)')"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Title and subtitle') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-text-field
                v-model="form.title"
                class="mb-4"
                :loading="form.processing"
                :error-messages="form.errors.title"
                :label="$t('Title')"
                outlined
                :hint="$t('Enter a title that will be displayed on the landing page')"
                persistent-hint
                :counter="60"
                :maxlength="60"
              />

              <v-textarea
                v-model="form.subtitle"
                :loading="form.processing"
                :error-messages="form.errors.subtitle"
                :label="$t('Subtitle')"
                outlined
                :hint="$t('Enter a subtitle that will be displayed on the landing page below the title')"
                persistent-hint
                rows="3"
                :counter="150"
                :maxlength="150"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Link to your website') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="form.website_url"
                    :loading="form.processing"
                    :error-messages="form.errors.website_url"
                    hide-details="auto"
                    class="mb-2"
                    label="URL"
                    outlined
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="form.website_link_text"
                    :loading="form.processing"
                    :error-messages="form.errors.website_link_text"
                    hide-details="auto"
                    class="mb-2"
                    :label="$t('Link text')"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Styling options') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <color-picker
                v-model="form.primary_color"
                class="mb-2"
                default-value="#d24919"
                :label="$t('Primary color')"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Custom CSS') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <code-editor
                v-model="form.css"
                language="css"
                style="min-height: 450px"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Passwords') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <manage-portal-passwords
                :faq-id="faq.id"
                :passwords-enabled="faq.portal_passwords_enabled"
                :passwords="faq.portal_passwords"
                @update:passwords-enabled="(value) => form.passwords_enabled = value"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>

      <portal to="faq-settings-layout-submit">
        <v-btn
          type="submit"
          color="primary"
          :loading="form.processing"
          :disabled="form.processing"
          @click="submit"
        >
          {{ $t('Save changes') }}
        </v-btn>
      </portal>
    </v-form>
  </div>
</template>

<script>
import Layout from '@/components/shared/TheLayout';
import TabsLayout from '@/components/faq/TabsLayout';
import SettingsLayout from '@/components/faq/settings/TabsLayout';
import ManagePortalPasswords from '@/components/faq/ManagePortalPasswords';
import unsavedFormWarningMixin from '@/mixins/faq/unsaved-form-warning';

export default {
  components: {
    ManagePortalPasswords
  },

  mixins: [ unsavedFormWarningMixin ],

  layout: (h, page) => h(Layout, {
    props: {
      noWrappingContainer: true,
      pageTitle: page.data.props.faq.default_translation.name
    }
  }, [h(TabsLayout, {
    props: {
      activeTab: 'settings',
      faq: page.data.props.faq,
    }
  }, [h(SettingsLayout, {
      props: {
        faq: page.data.props.faq,
        activeTab: 'portal',
      }
    }, [page])
  ])]),

  props: {
    faq: {
      type: Object,
      required: true
    },
    permissions: {
      type: Object,
      required: true
    },
    images: {
      type: Array,
      required: true
    },
    portalDomain: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      subdomainValid: null,
      showValidSubdomainSuccessMessage: false,

      validateSubdomainForm: this.$inertia.form({
        subdomain: this.faq.portal_subdomain
      }),

      form: this.$inertia.form({
        enabled: this.faq.portal_enabled,
        primary_color: this.faq.portal_primary_color,
        logo_id: this.faq.portal_logo_upload_id,
        favicon_id: this.faq.portal_favicon_upload_id,
        title: this.faq.default_translation.portal_title,
        subtitle: this.faq.default_translation.portal_subtitle,
        subdomain: this.faq.portal_subdomain,
        website_url: this.faq.portal_website_url,
        website_link_text: this.faq.default_translation.portal_website_link_text,
        css: this.faq.portal_css,
        user_feedback: this.faq.portal_user_feedback,
        contact_info: this.faq.portal_contact_info,
        contact_form: this.faq.portal_contact_form,
        passwords_enabled: this.faq.portal_passwords_enabled,
      }),
    }
  },

  computed: {
    validateButtonDisabled() {
      return (
        this.validateSubdomainForm.processing ||
        ! this.validateSubdomainForm.subdomain ||
        this.faq.portal_subdomain === this.validateSubdomainForm.subdomain
      );
    },

    subdomainSuccessMessage() {
      if (
        this.subdomainValid === true &&
        this.showValidSubdomainSuccessMessage === true
      ) {
        return ['This subdomain is available'];
      }

      return [];
    },

    subdomainErrorMessage() {
      return (
        this.validateSubdomainForm.errors.subdomain ||
        this.form.errors.subdomain
      );
    }
  },

  created() {
    this.$root.preview.faq.component = 'portal';

    window.onbeforeunload = () => {
      return this.form.isDirty || this.validateSubdomainForm.isDirty ? '' : null;
    }
  },

  methods: {
    submit() {
      const url = this.route('faq-settings.portal.update', this.faq.id);
      this.form.patch(url.toString(), {
        preserveState: true,
        onSuccess: () => {
          this.subdomainValid = false;
          this.showValidSubdomainSuccessMessage = false;
          this.$root.refreshFaqPreview();
        }
      });
    },

    async validateSubdomain() {
      const url = this.route('settings.portal.validate-subdomain');

      this.validateSubdomainForm.post(url.toString(), {
        preserveState: true,
        onSuccess: () => {
          this.subdomainValid = true;
          this.showValidSubdomainSuccessMessage = true;
        },
        onError: () => {
          this.subdomainValid = false;
          this.$nextTick(() => this.$refs.subdomainInput.focus());
        }
      });
    },

    handleSubdomainInput(value) {
      this.form.subdomain = value;
      this.subdomainValid = false;
      this.form.clearErrors('subdomain');
      this.validateSubdomainForm.clearErrors();
    }
  },
}
</script>

<style>
  .subdomain input {
    text-align: right !important;
  }
</style>